import { Component, OnInit, ViewChild } from '@angular/core';
import { cloneDeep, indexOf } from 'lodash';
import { MessageService, TreeNode } from 'primeng';
import { Message } from '../../classes/Message';
import { IOrganisationTag } from '../../interfaces/organisation-tag';
import { ITagEntity } from '../../interfaces/tag-entity';
import {
  OrganisationOrderPeferenceService,
  OrganisationTagsService,
  SupplierService,
  TaggingService,
} from '../../services';
import { TaggingSelectSupplierComponent } from '../tagging-select-supplier/tagging-select-supplier.component';
import { ISupplierOrderPreference } from '../../interfaces/organisation-order-preference';
import { Router } from '@angular/router';
import { AuditOperationTypeEnum } from '../../enums/audit-operation-type.enum';

@Component({
  selector: 'supplier-tagging',
  templateUrl: 'supplier-tagging.component.html',
  styleUrls: ['supplier-tagging.component.css'],
})
export class SupplierTaggingComponent implements OnInit {
  constructor(
    private supplierService: SupplierService,
    private taggingService: TaggingService,
    private organisationTagsService: OrganisationTagsService,
    private messageService: MessageService,
    private organisationOrderPreferenceService: OrganisationOrderPeferenceService,
    private router: Router,
  ) {}

  public selectedSuppliers: any[];
  tags: ITagEntity[];
  selectedTags: ITagEntity[] = [];

  public selectedCustomers: any[] = [];
  public includeCustomersReason: string;

  public driverIcon = {
    url: 'assets/images/driver.png',
    scaledSize: { width: 40, height: 40 },
  };

  showCustomerDialog: boolean;

  tagsLockedMsg =
    'Not all tag operations could be completed as some are locked by Network Assurance';

  @ViewChild('taggingSelectSupplier') taggingSelectSupplier: TaggingSelectSupplierComponent;

  ngOnInit() {
    this.taggingService.getAllTags().subscribe((result) => {
      this.tags = result;
    });
  }

  searchSupplier() {}

  clearSupplierFilter() {}

  onTagCheckChanged(event, tag: ITagEntity) {
    if (event && indexOf(this.selectedTags, tag) < 0) {
      this.selectedTags.push(tag);
    } else if (!event) {
      this.selectedTags = this.selectedTags.filter((x) => {
        return x != tag;
      });
    }
  }

  selectCustomerInclusions() {
    this.showCustomerDialog = true;
  }

  applyGeneralInclusions() {
    // ensure there is a reason for the exclusion
    if (
      !this.includeCustomersReason ||
      (this.includeCustomersReason && this.includeCustomersReason.trim() == '')
    ) {
      this.messageService.clear();
      this.messageService.add(
        Message.Error(
          'Please add a reason for the customer inclusion in the supplied text area.',
          'No reason given for inclusion',
        ),
      );
      return;
    }

    let promises: Promise<any>[] = [];

    // save prefs for each selected supplier in the list one at a time
    this.selectedSuppliers.forEach((x) => {
      // create a list of order preference records, one for each customer/dept/team in the inclusion list
      var prefs = this.selectedCustomers.map((y) => {
        return <ISupplierOrderPreference>{
          organisationId: x.id,
          targetOrganisationId: y.data.customerId,
          targetCustomerDepartmentId: y.data.departmentId,
          targetCustomerTeamId: y.data.teamId,
          reason: this.includeCustomersReason,
        };
      });

      let data = cloneDeep(x);
      data.supplierOrderPreferences = prefs;

      promises.push(this.organisationOrderPreferenceService.saveSupplierOrderPreferences(data));
    });

    Promise.all(promises).then((results) => {
      this.taggingSelectSupplier.refreshData();
    });
  }

  removeGeneralInclusions() {
    this.selectedSuppliers.forEach((x) => {
      let data = cloneDeep(x);
      data.supplierOrderPreferences.length = 0;

      this.organisationOrderPreferenceService.saveSupplierOrderPreferences(data).then((result) => {
        this.taggingSelectSupplier.refreshData();
      });
    });
  }

  async applyGeneralTags() {
    const supplierDTOs = [];
    // apply tags to selected suppliers
    this.selectedSuppliers.forEach((x) => {
      let supplierDTO = cloneDeep(x);

      // create tag data dtos from tags
      this.setOrgTags(supplierDTO);
      supplierDTO.operationType = AuditOperationTypeEnum.Add;

      supplierDTOs.push(supplierDTO);
    });

    const result = await this.organisationTagsService.saveSupplierTags(supplierDTOs);
    if (!result) {
      this.messageService.add(Message.Error(this.tagsLockedMsg, 'Tags Locked'));
    }

    this.taggingSelectSupplier.refreshData();
  }

  async removeGeneralTags() {
    const supplierDTOs = [];
    this.selectedSuppliers.forEach((x) => {
      let supplierDTO = cloneDeep(x);

      // create tag data dtos from tags
      supplierDTO.operationType = AuditOperationTypeEnum.Delete;

      supplierDTOs.push(supplierDTO);
    });

    const result = await this.organisationTagsService.saveSupplierTags(supplierDTOs);
    if (!result) {
      this.messageService.add(Message.Error(this.tagsLockedMsg, 'Tags Locked'));
    }

    this.taggingSelectSupplier.refreshData();
  }

  private setOrgTags(supplierDTO) {
    let tags = cloneDeep(this.selectedTags);

    var orgTags = tags.map((y) => {
      return <IOrganisationTag>{
        organisationId: supplierDTO.id,
        tagEntityId: y.id,
      };
    });

    supplierDTO.organisationTags = orgTags;
  }

  onSelectedSuppliersChanged(selected: any[]) {
    this.selectedSuppliers = selected;
  }

  onCustomerSelectionDialogClosed() {}

  onSelectedCustomersChanged(selected: any[]) {
    //console.log("Selected customers: ", selected);
    this.selectedCustomers = selected;
  }

  getCustomerName(customer: TreeNode) {
    let resultObj = { str: '' };
    this.getNodeNameRecursive(customer, resultObj);

    return resultObj.str;
  }

  getNodeNameRecursive(node: TreeNode, resultObj: { str: string }) {
    if (node.parent) this.getNodeNameRecursive(node.parent, resultObj);

    resultObj.str = resultObj.str + (resultObj.str.length > 0 ? ' - ' : '') + node.data.name;
  }

  editTags() {
    this.router.navigate(['app/tagging-admin']);
  }

  onRemoveInclusions() {
    this.removeGeneralInclusions();
  }

  clearCustomerSelection() {
    this.selectedCustomers = [];
  }
}
